import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { CustomerAuthService } from './auth.service';
import { environment } from '../../environments/environment';

const BASE_HEADERS: Record<string, string> = {
  // 'noChange' so MongoDB's `_id` / snake_case FK fields are not rewritten (matches the admin app).
  'x-am-response-case': 'noChange',
  'x-am-content-type-response': 'application/json',
};

// Public auth endpoints — never attach a token (and never bounce on their 401s).
const PUBLIC_PATHS = ['/customer-login', '/customer-register'];

/**
 * Attaches the customer's tokens to backend calls:
 *   x-am-authorization      = customer-scoped AM token (CRUD access)
 *   x-am-user-authorization = AM_DB identity token
 * On a 401/403 against an authenticated call, the session is cleared and the user is routed to
 * /login. Only requests to the configured backend host are touched; the public /catalog and the
 * auth endpoints pass through without a token.
 */
export const customerAuthInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.apiBaseUrl)) return next(req);

  const auth = inject(CustomerAuthService);
  const router = inject(Router);

  if (PUBLIC_PATHS.some((p) => req.url.includes(p))) {
    return next(req.clone({ setHeaders: BASE_HEADERS }));
  }

  const headers = { ...BASE_HEADERS };
  const apiToken = auth.apiToken();
  const userToken = auth.userToken();
  if (apiToken) headers['x-am-authorization'] = apiToken;
  if (userToken) headers['x-am-user-authorization'] = userToken;

  const wasAuthed = !!apiToken;
  return next(req.clone({ setHeaders: headers })).pipe(
    catchError((err) => {
      const status = (err as { status?: number })?.status;
      if ((status === 401 || status === 403) && wasAuthed) {
        auth.logout();
        router.navigate(['/login']);
      }
      return throwError(() => err);
    }),
  );
};
